import type { ReactNode } from 'react';

/**
 * 입력칸·값 표시 (19회차, 콘솔 검토 A-4(f)·D-1·D-5).
 *
 * 16회차까지 `Field`가 네 벌, `Stat`이 두 벌 있었고 라벨 위치도 두 가지였다.
 * 입력 요소의 포커스는 테두리 색만 바뀌어 링이 없었다 — 이제 전역 `:focus-visible`이 준다.
 */

/** 폼 한 칸. 라벨은 항상 위에 둔다 */
export function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  /** 라벨 아래 한 줄 설명 */
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <label htmlFor={htmlFor} className="text-xs text-muted">
        {label}
      </label>
      {children}
      {hint ? <p className="whitespace-pre-line text-xs text-muted">{hint}</p> : null}
    </div>
  );
}

/** 입력칸·고르는 칸 공통 모양. 테두리는 구조선이라 3:1을 넘는 회색이다 */
export const CONTROL =
  'w-full rounded-lg border border-line bg-panel px-3 py-2 text-sm text-ink ' +
  'transition-colors duration-150 outline-none hover:border-ink';

export function TextInput({
  id,
  value,
  onChange,
  placeholder,
  type,
  inputMode,
  name,
  autoComplete,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** `password`는 로그인 화면(51회차)이 쓴다 */
  type?: 'text' | 'datetime-local' | 'password';
  inputMode?: 'numeric';
  name?: string;
  /** 브라우저 자동완성 힌트. 로그인 화면이 `username`·`current-password`를 준다 */
  autoComplete?: string;
}) {
  return (
    <input
      id={id}
      name={name}
      type={type ?? 'text'}
      value={value}
      inputMode={inputMode}
      autoComplete={autoComplete}
      placeholder={placeholder}
      // 기간 입력은 초까지 받는다 (23-A회차 리뷰 6). 없으면 브라우저가 초를 잘라 버린다
      step={type === 'datetime-local' ? 1 : undefined}
      onChange={(event) => onChange(event.target.value)}
      className={CONTROL}
    />
  );
}

export function Select({
  id,
  value,
  onChange,
  options,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly { value: string; label: string }[];
}) {
  return (
    <select
      id={id}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={CONTROL}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

/** 켜고 끄는 것. 오류만 보기·sse 포함처럼 한 번에 하나만 바뀌는 조건 */
export function Toggle({
  id,
  checked,
  onChange,
  label,
  hint,
}: {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  hint?: string;
}) {
  return (
    <label
      htmlFor={id}
      title={hint}
      className="inline-flex cursor-pointer items-center gap-2 text-sm text-ink"
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="size-4 accent-accent"
      />
      {label}
    </label>
  );
}

/** 이름과 값 한 쌍. `<dl>` 안에서 쓴다 */
export function Value({
  label,
  value,
  tone,
  mono,
  hint,
}: {
  label: string;
  value: ReactNode;
  tone?: string;
  mono?: boolean;
  hint?: string;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted">{label}</dt>
      <dd
        className={`mt-1 break-all ${mono ? 'font-mono text-xs' : 'text-sm'} ${tone ?? 'text-ink'}`}
      >
        {value}
      </dd>
      {hint !== undefined ? (
        <dd className="mt-0.5 whitespace-pre-line break-words text-xs text-muted">{hint}</dd>
      ) : null}
    </div>
  );
}

/**
 * 숫자 타일. 큰 숫자 하나와 이름 하나.
 *
 * 경고는 색만으로 알리지 않는다(콘솔 검토 D-3). 값 옆에 글자를 붙인다.
 */
export function Stat({
  label,
  value,
  note,
  level = 'normal',
  warnLabel,
}: {
  label: string;
  value: string;
  note?: string;
  level?: 'normal' | 'warn' | 'danger';
  /** 경고일 때 값 옆에 붙는 글자. 색만으로 알리지 않기 위해 */
  warnLabel?: string;
}) {
  const tone = level === 'danger' ? 'text-error' : level === 'warn' ? 'text-fail' : 'text-ink';
  return (
    <div className="rounded-xl border border-hair bg-panel px-4 py-3">
      <p className="whitespace-nowrap text-xs text-muted">{label}</p>
      <p className={`tabular mt-1 text-lg font-semibold ${tone}`}>
        {value}
        {level !== 'normal' && warnLabel ? (
          <span className="ml-1.5 text-xs font-normal">{warnLabel}</span>
        ) : null}
      </p>
      {note ? (
        <p className="mt-0.5 whitespace-pre-line break-words text-[11px] text-muted">{note}</p>
      ) : null}
    </div>
  );
}
